import { usePatientsStore } from "@/lib/stores/patients";
import { useBillsStore } from "@/lib/stores/bills";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { useState, useEffect } from "react";
import { formatPatientId } from '@/lib/utils/patientId';
import { Link } from "react-router-dom";
import { safeFormatDate } from '@/lib/utils/dateUtils';
import { Clock } from "lucide-react";

export function PatientHistory() {
  const { patients } = usePatientsStore();
  const { bills, fetchBills } = useBillsStore();
  const [searchQuery, setSearchQuery] = useState("");
  
  // Fetch bills data when component mounts and when bills array changes
  useEffect(() => {
    fetchBills();
  }, [fetchBills]);
  
  // Force refresh bills if empty after initial load
  useEffect(() => {
    if (bills.length === 0) {
      console.log('No bills found, attempting to fetch again...');
      fetchBills();
    }
  }, [bills.length, fetchBills]);
  
  // Helper function to check if patient has bills
  const hasBillGenerated = (patientId: string) => {
    return bills.some(bill => bill.patientId === patientId && bill.status === 'active');
  };
  
  // Helper function to get color-coded payment status (CURRENT VISIT ONLY)
  const getPaymentStatus = (patient: any) => {
    const servicesCost = patient.services?.reduce((total: number, service: any) => total + (Number(service.price) || 0), 0) || 0;
    const amountPaid = Number(patient.amountPaid) || 0;
    const dueAmount = Math.max(0, servicesCost - amountPaid);
    const hasBill = hasBillGenerated(patient.id);
    
    
    // For completed patients, always check bills table first
    if (patient.status === 'completed') {
      const patientBills = bills.filter(b => b.patientId === patient.id && b.status === 'active');
      const totalBillAmount = patientBills.reduce((sum, bill) => sum + bill.amountPaid, 0);
      
      console.log('Bill calculation for completed patient:', {
        patientId: patient.id,
        patientBills,
        totalBillAmount
      });
      
      if (totalBillAmount > 0) {
        return (
          <span className="text-green-600 font-semibold">
            Billed: ₹{totalBillAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </span>
        );
      }
      
      // If no bills found but patient has payment history, show that
      const totalPaymentHistory = (patient.paymentHistory || []).reduce((total: number, payment: any) => {
        return total + (Number(payment.amount) || 0);
      }, 0);
      
      if (totalPaymentHistory > 0) {
        return (
          <span className="text-blue-600 font-semibold">
            Paid: ₹{totalPaymentHistory.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </span>
        );
      }
      
      // If no bills and no payment history, check if there's a due amount
      if (dueAmount > 0) {
        return (
          <span className="text-red-600 font-semibold">
            Due: ₹{dueAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </span>
        );
      }
      
      return <span className="text-gray-500">-</span>;
    }
    
    // For non-completed patients with no services, check bills table
    if (servicesCost === 0) {
      if (hasBill) {
        // Get bill information for completed patients
        const patientBills = bills.filter(b => b.patientId === patient.id && b.status === 'active');
        const totalBillAmount = patientBills.reduce((sum, bill) => sum + bill.amountPaid, 0);
        
        console.log('Bill calculation for patient with no services:', {
          patientId: patient.id,
          patientBills,
          totalBillAmount
        });
        
        if (totalBillAmount > 0) {
          return (
            <span className="text-green-600 font-semibold">
              Billed: ₹{totalBillAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
          );
        }
      }
      return <span className="text-gray-500">-</span>;
    }
    
    if (dueAmount === 0 && servicesCost > 0) {
      // Payment is complete for current visit
      if (hasBill) {
        // Bill generated - Green
        return (
          <span className="text-green-600 font-semibold">
            Billed: ₹{amountPaid.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </span>
        );
      } else {
        // No bill generated - Blue
        return (
          <span className="text-blue-600 font-semibold">
            Paid: ₹{amountPaid.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </span>
        );
      }
    } else {
      // Payment due for current visit - Red
      return (
        <span className="text-red-600 font-semibold">
          Due: ₹{dueAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </span>
      );
    }
  };
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "waiting":
        return (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 text-xs px-2 py-0.5">
            Waiting
          </Badge>
        );
      case "in-consultation":
        return (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs px-2 py-0.5">
            In Consultation
          </Badge>
        );
      case "completed":
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs px-2 py-0.5">
            Completed
          </Badge>
        );
      case "no-show":
        return (
          <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 text-xs px-2 py-0.5">
            No Show
          </Badge>
        );
      default:
        return null;
    }
  };

  const filteredPatients = patients.filter(patient => {
    const searchLower = searchQuery.toLowerCase();
    return (
      patient.name.toLowerCase().includes(searchLower) ||
      patient.contactNumber.includes(searchQuery) ||
      patient.id.toLowerCase().includes(searchLower) ||
      patient.reason?.toLowerCase().includes(searchLower) ||
      patient.referralDoctor?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Patient History</h2>
        <div className="relative w-64">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search patients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>
      
      {filteredPatients.length === 0 ? (
        <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900">No patient records</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchQuery ? "No patients match your search" : "Registered patients will appear here."}
            </p>
          </div>
        </div>
      ) : (
        <div className="rounded-md border overflow-auto max-h-[60vh]">
          <Table className="table-fixed w-full">
            <TableHeader className="sticky top-0 bg-background">
              <TableRow className="h-10">
                <TableHead className="py-2 px-3 text-xs font-medium w-24">Patient ID</TableHead>
                <TableHead className="py-2 px-3 text-xs font-medium w-40">Patient Name</TableHead>
                <TableHead className="py-2 px-3 text-xs font-medium w-20">Age/Gender</TableHead>
                <TableHead className="py-2 px-3 text-xs font-medium w-24">Contact</TableHead>
                <TableHead className="py-2 px-3 text-xs font-medium w-28">Registered</TableHead>
                <TableHead className="py-2 px-3 text-xs font-medium w-32">Reason</TableHead>
                <TableHead className="py-2 px-3 text-xs font-medium w-20">Status</TableHead>
                <TableHead className="py-2 px-3 text-xs font-medium w-32">Payment Status</TableHead>
                <TableHead className="py-2 px-3 text-xs font-medium w-20">All Visits</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPatients.map((patient) => (
                <TableRow key={patient.id} className="hover:bg-gray-50 cursor-pointer h-12">
                  <TableCell className="py-2 px-3 w-24">
                    <span className="bg-hospital-primary/10 px-2 py-1 rounded-full text-hospital-primary font-medium text-xs">
                      {formatPatientId(patient.id)}
                    </span>
                  </TableCell>
                  <TableCell className="py-2 px-3 w-40">
                    <Link 
                      to={`/patient/${patient.id}`} 
                      className="font-medium hover:text-hospital-primary transition-colors text-sm truncate block"
                      title={patient.name}
                    >
                      {patient.name}
                    </Link>
                  </TableCell>
                  <TableCell className="py-2 px-3 w-20 text-sm">
                    {(() => {
                      // Extract just the number from age (remove "years" if present)
                      const ageNumber = typeof patient.age === 'string' 
                        ? patient.age.replace(/\s*years?/i, '').trim()
                        : patient.age;
                      // Capitalize first letter of gender
                      const genderFormatted = patient.gender 
                        ? patient.gender.charAt(0).toUpperCase() + patient.gender.slice(1).toLowerCase()
                        : patient.gender;
                      return `${ageNumber}/${genderFormatted}`;
                    })()}
                  </TableCell>
                  <TableCell className="py-2 px-3 w-24 text-sm">{patient.contactNumber || "-"}</TableCell>
                  <TableCell className="py-2 px-3 w-28">
                    <div className="flex items-center gap-1 text-gray-600">
                      <Clock className="h-3 w-3" />
                      <span className="text-xs">{safeFormatDate(patient.registeredAt)}</span>
                    </div>
                  </TableCell>
                  <TableCell className="py-2 px-3 w-32">
                    <div 
                      className="text-sm truncate" 
                      title={patient.reason}
                    >
                      {patient.reason}
                    </div>
                  </TableCell>
                  <TableCell className="py-2 px-3 w-20">{getStatusBadge(patient.status)}</TableCell>
                  <TableCell className="py-2 px-3 w-32 font-medium text-sm">
                    {getPaymentStatus(patient)}
                  </TableCell>
                  <TableCell className="py-2 px-3 w-20 text-sm">
                    {(() => {
                      // Calculate cumulative payments across ALL VISITS for master patient
                      const relatedPatients = patients.filter(p => 
                        (patient.masterPatientId && p.masterPatientId === patient.masterPatientId) || p.id === patient.id
                      );
                      
                      // Get all related patient IDs
                      const relatedPatientIds = relatedPatients.map(p => p.id);
                      
                      // 1. Sum all bills from bills table (completed visits)
                      const relatedBills = bills.filter(b => 
                        relatedPatientIds.includes(b.patientId) && b.status === 'active'
                      );
                      const billTotal = relatedBills.reduce((sum, bill) => sum + bill.amountPaid, 0);
                      
                      // 2. Sum current visit payments (active visits)
                      const currentVisitTotal = relatedPatients.reduce((sum, p) => {
                        const currentTotal = Number(p.amountPaid) || 0;
                        return sum + currentTotal;
                      }, 0);
                      
                      // 3. Total = Bills + Current visits
                      const totalRecordedPayments = billTotal + currentVisitTotal;
                      
                      return (
                        <span className="text-black bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5 text-xs">
                          ₹{totalRecordedPayments.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </span>
                      );
                    })()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
} 